import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../api/client";
import { theme, card, btnPrimary, btnSecondary } from "../../theme";
import { fmtGbp } from "../../utils/format";
import { frameworkLabel } from "../../nav/frameworkMeta";
import { downloadCsv } from "../../lib/registerExport";
import { clientNeedsAction } from "../../lib/portfolioStatus";
import { useConsultantShell } from "./ConsultantShellContext";
import { ScopeToggle } from "./ScopeToggle";
import { scoreColor, type OrgSummary } from "./types";
import {
  ASSIGNMENT_ROLE_LABELS, errText, HEALTH_STYLE, PIPELINE_STYLE,
  type AssignmentRole, type ClientHealth, type PipelineRow, type StaffMember,
} from "./workspace";

// ── Clients as a table ────────────────────────────────────────────────────
// Sortable columns, saved-in-the-URL filters, paging, a CSV of exactly what is on
// screen, and bulk actions on the rows ticked: assign a consultant (firm:admin),
// queue compliance checks, send the clients a reminder, add to a group. Health comes from GET
// /consultant/workspace/health and the next milestone from /pipeline; every bulk
// action is one API call per client, re-checked there (tier, permissions, scope).

type SortKey = "name" | "health" | "score" | "change" | "lead" | "tier" | "standards" | "findings" | "next" | "revenue";

interface Row {
  org: OrgSummary;
  health: ClientHealth | null;
  lead: string;
  next: PipelineRow | null;
}

const PAGE = 50;

export function ClientsTable({ orgs, onOpen, selfOrgId }: { orgs: OrgSummary[]; onOpen: (id: string) => void; selfOrgId: string | null }) {
  const { scope, user, isFirmAdmin, permissions, reloadPortfolio, notify } = useConsultantShell();
  const [params, setParams] = useSearchParams();
  const [health, setHealth] = useState<Record<string, ClientHealth>>({});
  const [pipeline, setPipeline] = useState<PipelineRow[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "health", dir: 1 });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<"" | "assign" | "group" | "remind">("");
  const [reminderNote, setReminderNote] = useState("");
  const [assignTo, setAssignTo] = useState("");
  const [assignRole, setAssignRole] = useState<AssignmentRole>("lead");
  const [groups, setGroups] = useState<Array<{ id: string; name: string; organizations: Array<{ id: string }> }>>([]);
  const [groupId, setGroupId] = useState("");
  const [working, setWorking] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const search = params.get("q") ?? "";
  const status = params.get("filter") ?? "";
  const framework = params.get("framework") ?? "";
  const leadFilter = params.get("lead") ?? "";
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
    setPage(1);
  };

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    Promise.all([
      api.get<{ clients: Record<string, ClientHealth> }>("/consultant/workspace/health", { scope: "firm" }),
      api.get<{ pipeline: PipelineRow[] }>("/consultant/workspace/pipeline", { scope: "firm" }),
      api.get<{ staff: StaffMember[] }>("/consultant/workspace/staff"),
    ])
      .then(([h, p, s]) => { if (!cancelled) { setHealth(h.clients); setPipeline(p.pipeline); setStaff(s.staff); } })
      .catch((e) => { if (!cancelled) setLoadError(errText(e, "Could not load client health")); });
    return () => { cancelled = true; };
  }, [orgs]);

  const rows = useMemo<Row[]>(() => orgs.map((org) => {
    const lead = org.assignees?.find((a) => a.role === "lead")?.displayName ?? "";
    const next = pipeline
      .filter((p) => p.organizationId === org.id && p.nextDate)
      .sort((a, b) => (a.nextDate ?? "").localeCompare(b.nextDate ?? ""))[0] ?? null;
    return { org, health: health[org.id] ?? null, lead, next };
  }), [orgs, health, pipeline]);

  const frameworks = useMemo(() => [...new Set(orgs.flatMap((o) => o.frameworks.map((f) => f.frameworkId)))].sort(), [orgs]);
  const showRevenue = orgs.some((o) => o.monthlyBilledPence !== null);

  const filtered = rows.filter(({ org, health: h }) => {
    if (scope === "mine") {
      const a = org.assignees ?? [];
      if (a.length > 0 && !a.some((x) => x.userId === user.id)) return false;
    }
    if (search && !org.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (framework && !org.frameworks.some((f) => f.frameworkId === framework)) return false;
    if (leadFilter === "none" && (org.assignees ?? []).length > 0) return false;
    if (leadFilter && leadFilter !== "none" && !(org.assignees ?? []).some((a) => a.userId === leadFilter)) return false;
    switch (status) {
      case "attention": return clientNeedsAction(org);
      case "sector": return org.sectorSettled === false;
      case "at_risk": case "watch": case "healthy": case "unassessed": return h?.band === status;
      default: return true;
    }
  });

  const value = (r: Row, key: SortKey): string | number => {
    switch (key) {
      case "name": return r.org.name.toLowerCase();
      case "health": return r.health?.health ?? 999;
      case "score": return r.org.latestScore ?? -1;
      case "change": return r.health?.scoreChange30d ?? 0;
      case "lead": return r.lead || "~";
      case "tier": return r.org.serviceTier.level;
      case "standards": return r.org.frameworks.length;
      case "findings": return r.org.openFindings;
      case "next": return r.next?.nextDate ?? "9999";
      case "revenue": return r.org.monthlyBilledPence ?? 0;
    }
  };
  const sorted = [...filtered].sort((a, b) => {
    const x = value(a, sort.key), y = value(b, sort.key);
    return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
  });
  const pages = Math.max(1, Math.ceil(sorted.length / PAGE));
  const visible = sorted.slice((page - 1) * PAGE, page * PAGE);

  const exportCsv = () => downloadCsv<Row>("clients", [
    { header: "Client", value: (r) => r.org.name },
    { header: "Health", value: (r) => r.health?.health ?? "" },
    { header: "Health band", value: (r) => (r.health ? HEALTH_STYLE[r.health.band].label : "") },
    { header: "Why", value: (r) => r.health?.reasons.join("; ") ?? "" },
    { header: "SOA score (%)", value: (r) => r.org.latestScore ?? "" },
    { header: "Change in 30 days", value: (r) => r.health?.scoreChange30d ?? "" },
    { header: "Lead consultant", value: (r) => r.lead },
    { header: "Tier", value: (r) => r.org.serviceTier.name },
    { header: "Standards", value: (r) => r.org.frameworks.map((f) => frameworkLabel(f.frameworkId)).join("; ") },
    { header: "Open findings", value: (r) => r.org.openFindings },
    { header: "Next milestone", value: (r) => (r.next ? `${r.next.nextLabel} ${r.next.nextDate?.slice(0, 10)}` : "") },
    ...(showRevenue ? [{ header: "Monthly billed (GBP)", value: (r: Row) => ((r.org.monthlyBilledPence ?? 0) / 100).toFixed(2) }] : []),
  ], sorted);

  // ── Bulk actions ────────────────────────────────────────────────────────
  const chosen = sorted.filter((r) => selected.has(r.org.id)).map((r) => r.org);

  async function runBulk(label: string, each: (org: OrgSummary) => Promise<unknown>) {
    setWorking(true);
    setReport(null);
    const failed: string[] = [];
    for (const org of chosen) {
      try { await each(org); }
      catch (e) { failed.push(`${org.name} (${errText(e, "failed")})`); }
    }
    setWorking(false);
    setReport(failed.length ? `${label}: ${chosen.length - failed.length} done, ${failed.length} not — ${failed.join("; ")}` : `${label}: done for ${chosen.length} client${chosen.length !== 1 ? "s" : ""}.`);
    setSelected(new Set());
    setBulk("");
    reloadPortfolio();
  }

  const assign = () => {
    if (!assignTo) return;
    setWorking(true);
    api.post("/consultant/workspace/assignments/bulk", { organizationIds: chosen.map((o) => o.id), userId: assignTo, role: assignRole })
      .then(() => { setReport(`Assigned to ${chosen.length} client${chosen.length !== 1 ? "s" : ""}.`); setSelected(new Set()); setBulk(""); reloadPortfolio(); })
      .catch((e) => notify(errText(e, "Could not assign the consultant")))
      .finally(() => setWorking(false));
  };
  // One email to each chosen client's administrators listing what is waiting on them
  // (POST /consultant/workspace/reminders — at most one per client per day).
  const sendReminders = () => {
    setWorking(true);
    setReport(null);
    api.post<{ results: Array<{ organizationName: string; status: "sent" | "skipped"; reason?: string; recipients?: number }> }>(
      "/consultant/workspace/reminders", { organizationIds: chosen.map((o) => o.id), note: reminderNote.trim() || undefined })
      .then(({ results }) => {
        const sent = results.filter((r) => r.status === "sent");
        const skipped = results.filter((r) => r.status === "skipped");
        setReport(`Reminders sent to ${sent.length} client${sent.length !== 1 ? "s" : ""}${sent.length ? ` (${sent.reduce((n, r) => n + (r.recipients ?? 0), 0)} people)` : ""}.`
          + (skipped.length ? ` Not sent: ${skipped.map((r) => `${r.organizationName} — ${r.reason}`).join("; ")}.` : ""));
        setSelected(new Set());
        setBulk("");
        setReminderNote("");
      })
      .catch((e) => notify(errText(e, "Could not send the reminders")))
      .finally(() => setWorking(false));
  };
  const runChecks = () => runBulk("Compliance checks queued", (org) => api.post("/compliance/check-runs", {}, { organizationId: org.id }));
  const openGroupPicker = () => {
    setBulk("group");
    api.get<{ groups: typeof groups }>("/groups").then((d) => setGroups(d.groups)).catch((e) => notify(errText(e, "Could not load groups")));
  };
  const addToGroup = () => {
    const g = groups.find((x) => x.id === groupId);
    if (!g) return;
    const adding = chosen.filter((o) => !g.organizations.some((m) => m.id === o.id));
    if (adding.length === 0) { setReport(`Already in ${g.name}.`); return; }
    // Joining a group opens the organisation to the parent's people who hold group access: say so
    // before it happens, as the Groups page does (REP-41).
    const names = adding.map((o) => o.name).join(", ");
    if (!window.confirm(`Add ${names} to ${g.name}?

People at the group's parent organisation with group access will be able to see ${adding.length === 1 ? "this organisation" : "these organisations"}, and its group administrators to work in ${adding.length === 1 ? "it" : "them"}. Check the group's consolidated headcount and turnover afterwards.`)) return;
    setWorking(true);
    const ids = [...new Set([...g.organizations.map((o) => o.id), ...chosen.map((o) => o.id)])];
    api.patch(`/groups/${g.id}`, { organizationIds: ids })
      .then(() => { setReport(`Added to ${g.name}.`); setSelected(new Set()); setBulk(""); })
      .catch((e) => notify(errText(e, "Could not add to the group")))
      .finally(() => setWorking(false));
  };

  // ── Render ──────────────────────────────────────────────────────────────
  const input: React.CSSProperties = { padding: "6px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12.5, maxWidth: 200 };
  const th = (key: SortKey, label: string, align: "left" | "right" = "left") => (
    <th scope="col" aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}
      style={{ textAlign: align, padding: "8px 10px", fontSize: 11, fontWeight: 700, color: theme.color.faint, whiteSpace: "nowrap", borderBottom: `1px solid ${theme.color.line}` }}>
      <button onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "inherit", font: "inherit" }}>
        {label}{sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}
      </button>
    </th>
  );
  const td: React.CSSProperties = { padding: "9px 10px", fontSize: 12.5, color: theme.color.text, borderBottom: `1px solid ${theme.color.border}`, verticalAlign: "top" };
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.org.id));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input value={search} onChange={(e) => setParam("q", e.target.value)} placeholder="Search clients" aria-label="Search clients" style={{ ...input, minWidth: 180 }} />
        <select value={status} onChange={(e) => setParam("filter", e.target.value)} aria-label="Status" style={input}>
          <option value="">Every client</option>
          <option value="attention">Needs action</option>
          <option value="at_risk">Health: at risk</option>
          <option value="watch">Health: watch</option>
          <option value="healthy">Health: healthy</option>
          <option value="unassessed">Not assessed</option>
          <option value="sector">No settled sector</option>
        </select>
        <select value={framework} onChange={(e) => setParam("framework", e.target.value)} aria-label="Standard" style={input}>
          <option value="">Every standard</option>
          {frameworks.map((f) => <option key={f} value={f}>{frameworkLabel(f)}</option>)}
        </select>
        <select value={leadFilter} onChange={(e) => setParam("lead", e.target.value)} aria-label="Consultant" style={input}>
          <option value="">Any consultant</option>
          <option value="none">Nobody assigned</option>
          {staff.map((s) => <option key={s.id} value={s.id}>{s.displayName}</option>)}
        </select>
        <ScopeToggle compact />
        <button onClick={exportCsv} style={{ ...btnSecondary, padding: "6px 12px", fontSize: 12, marginLeft: "auto" }}>Export CSV</button>
      </div>

      {loadError && <div style={{ fontSize: 12.5, color: theme.color.warningText }}>{loadError} — health and milestones are missing below.</div>}
      {report && <div role="status" style={{ fontSize: 12.5, color: theme.color.text, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 12px" }}>{report}</div>}

      {selected.size > 0 && (
        <div style={{ ...card, padding: "10px 14px", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: theme.color.heading }}>{selected.size} selected</span>
          {isFirmAdmin && <button disabled={working} onClick={() => setBulk(bulk === "assign" ? "" : "assign")} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>Assign consultant</button>}
          {permissions.includes("compliance:run") && <button disabled={working} onClick={runChecks} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>{working ? "Working…" : "Run compliance checks"}</button>}
          {permissions.includes("org:admin") && <button disabled={working} onClick={() => setBulk(bulk === "remind" ? "" : "remind")} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>Send reminder</button>}
          {permissions.includes("org:admin") && <button disabled={working} onClick={openGroupPicker} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12 }}>Add to group</button>}
          <button onClick={() => setSelected(new Set())} style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12, marginLeft: "auto" }}>Clear</button>
          {bulk === "assign" && (
            <div style={{ display: "flex", gap: 6, width: "100%", flexWrap: "wrap" }}>
              <select value={assignTo} onChange={(e) => setAssignTo(e.target.value)} aria-label="Consultant to assign" style={input}>
                <option value="">Choose a consultant…</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.displayName} ({s.assignedCount} clients)</option>)}
              </select>
              <select value={assignRole} onChange={(e) => setAssignRole(e.target.value as AssignmentRole)} aria-label="Role" style={input}>
                {(Object.keys(ASSIGNMENT_ROLE_LABELS) as AssignmentRole[]).map((r) => <option key={r} value={r}>{ASSIGNMENT_ROLE_LABELS[r]}</option>)}
              </select>
              <button disabled={!assignTo || working} onClick={assign} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, opacity: !assignTo ? 0.5 : 1 }}>Assign</button>
              {assignRole === "lead" && <span style={{ fontSize: 11.5, color: theme.color.subtle, alignSelf: "center" }}>A client's previous lead becomes supporting.</span>}
            </div>
          )}
          {bulk === "remind" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
              <div style={{ fontSize: 12, color: theme.color.subtle }}>
                Each client's administrators get one email listing the actions waiting for them, with your note. A client already reminded today is skipped.
              </div>
              <textarea value={reminderNote} onChange={(e) => setReminderNote(e.target.value)} maxLength={1000} rows={2}
                placeholder="Optional note, e.g. what you need before the audit" aria-label="Reminder note"
                style={{ ...input, maxWidth: "100%", resize: "vertical", fontFamily: theme.font }} />
              <div><button disabled={working} onClick={sendReminders} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12 }}>{working ? "Sending…" : `Send to ${chosen.length} client${chosen.length !== 1 ? "s" : ""}`}</button></div>
            </div>
          )}
          {bulk === "group" && (
            <div style={{ display: "flex", gap: 6, width: "100%", flexWrap: "wrap" }}>
              <select value={groupId} onChange={(e) => setGroupId(e.target.value)} aria-label="Group" style={input}>
                <option value="">Choose a group…</option>
                {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <button disabled={!groupId || working} onClick={addToGroup} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, opacity: !groupId ? 0.5 : 1 }}>Add</button>
            </div>
          )}
        </div>
      )}

      <div style={{ ...card, padding: 0, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 860 }}>
          <thead>
            <tr>
              <th scope="col" style={{ width: 32, padding: "8px 10px", borderBottom: `1px solid ${theme.color.line}` }}>
                <input type="checkbox" aria-label="Select all on this page" checked={allVisibleSelected}
                  onChange={(e) => setSelected((s) => { const n = new Set(s); for (const r of visible) { if (e.target.checked) n.add(r.org.id); else n.delete(r.org.id); } return n; })} />
              </th>
              {th("name", "Client")}
              {th("health", "Health")}
              {th("score", "Score", "right")}
              {th("change", "30 days", "right")}
              {th("lead", "Lead")}
              {th("tier", "Tier")}
              {th("standards", "Standards")}
              {th("findings", "Findings", "right")}
              {th("next", "Next milestone")}
              {showRevenue && th("revenue", "Billed / mo", "right")}
              <th scope="col" style={{ width: 1, borderBottom: `1px solid ${theme.color.line}` }}><span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Open</span></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={showRevenue ? 12 : 11} style={{ ...td, textAlign: "center", color: theme.color.subtle, padding: 28 }}>No clients match.</td></tr>
            )}
            {visible.map(({ org, health: h, lead, next }) => {
              const hs = h ? HEALTH_STYLE[h.band] : null;
              const change = h?.scoreChange30d ?? null;
              return (
                <tr
                  key={org.id}
                  onClick={(e) => {
                    // The whole row opens the client; the checkbox and buttons keep their own job.
                    if ((e.target as HTMLElement).closest("input, button, a, select, label")) return;
                    onOpen(org.id);
                  }}
                  onMouseEnter={() => setHoveredId(org.id)}
                  onMouseLeave={() => setHoveredId((id) => (id === org.id ? null : id))}
                  style={{ cursor: "pointer", background: hoveredId === org.id ? theme.color.accentBg : undefined, transition: "background 0.1s" }}
                >
                  <td style={td}>
                    <input type="checkbox" aria-label={`Select ${org.name}`} checked={selected.has(org.id)}
                      onChange={(e) => setSelected((s) => { const n = new Set(s); if (e.target.checked) n.add(org.id); else n.delete(org.id); return n; })} />
                  </td>
                  <td style={td}>
                    <button onClick={() => onOpen(org.id)} title={`Open ${org.name}`} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontWeight: 600, color: theme.color.accentText, fontSize: 12.5, textAlign: "left", fontFamily: theme.font, textDecoration: hoveredId === org.id ? "underline" : "none" }}>{org.name}</button>
                    {org.id === selfOrgId && <span style={{ marginLeft: 6, fontSize: 10.5, color: theme.color.faint }}>your company</span>}
                    {org.sectorSettled === false && <div style={{ fontSize: 11, color: theme.color.warningText }}>No settled sector</div>}
                  </td>
                  <td style={td} title={h?.reasons.join("\n") || undefined}>
                    {hs && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 3, background: hs.bg, color: hs.color, whiteSpace: "nowrap" }}>
                        {h?.health !== null && h?.health !== undefined ? `${h.health} · ` : ""}{hs.label}
                      </span>
                    )}
                    {h && h.reasons[0] && h.band !== "healthy" && <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 3 }}>{h.reasons[0]}</div>}
                  </td>
                  <td style={{ ...td, textAlign: "right", fontWeight: 700, color: scoreColor(org.latestScore) }}>{org.latestScore !== null ? `${org.latestScore}%` : "—"}</td>
                  <td style={{ ...td, textAlign: "right", color: change === null ? theme.color.faint : change < 0 ? theme.color.dangerText : change > 0 ? theme.color.successText : theme.color.subtle }}>
                    {change === null ? "—" : change > 0 ? `+${change}` : String(change)}
                  </td>
                  <td style={td}>
                    {lead || <span style={{ color: theme.color.faint }}>{(org.assignees ?? []).length ? "No lead" : "Unassigned"}</span>}
                    {(org.assignees ?? []).filter((a) => a.role !== "lead").length > 0 && (
                      <span title={(org.assignees ?? []).filter((a) => a.role !== "lead").map((a) => `${a.displayName} (${a.role})`).join(", ")}
                        style={{ marginLeft: 6, fontSize: 11, color: theme.color.subtle }}>
                        +{(org.assignees ?? []).filter((a) => a.role !== "lead").length}
                      </span>
                    )}
                  </td>
                  <td style={{ ...td, whiteSpace: "nowrap" }}>{org.serviceTier.name}</td>
                  <td style={td}>{org.frameworks.map((f) => frameworkLabel(f.frameworkId)).join(", ") || "—"}</td>
                  <td style={{ ...td, textAlign: "right", color: org.openFindings > 0 ? theme.color.dangerText : theme.color.subtle }}>{org.openFindings}</td>
                  <td style={td}>
                    {next ? (
                      <span>
                        <span style={{ fontSize: 10.5, fontWeight: 700, padding: "1px 7px", borderRadius: 3, background: PIPELINE_STYLE[next.stage]?.bg, color: PIPELINE_STYLE[next.stage]?.color }}>{next.stageLabel}</span>
                        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>{next.nextLabel} · {new Date(next.nextDate!).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</div>
                      </span>
                    ) : <span style={{ color: theme.color.faint }}>—</span>}
                  </td>
                  {showRevenue && <td style={{ ...td, textAlign: "right" }}>{fmtGbp(org.monthlyBilledPence ?? 0)}</td>}
                  <td style={{ ...td, textAlign: "right", verticalAlign: "middle" }}>
                    <button type="button" onClick={() => onOpen(org.id)} aria-label={`Open ${org.name}`}
                      style={{ ...btnSecondary, padding: "4px 12px", fontSize: 12, whiteSpace: "nowrap" }}>
                      Open
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", fontSize: 12.5, color: theme.color.subtle }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12, opacity: page <= 1 ? 0.5 : 1 }}>Previous</button>
          Page {page} of {pages} · {sorted.length} clients
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 12, opacity: page >= pages ? 0.5 : 1 }}>Next</button>
        </div>
      )}
    </div>
  );
}
